import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { faTrophy, faRankingStar, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import API from '../../api';

// Backend returns "N/A" strings for missing ranks; normalize to null.
function normalize(value) {
    if (value === 'N/A' || value === null || value === undefined) return null;
    return value;
}

function CTFStatsSection() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();

        const fetchStats = async () => {
            try {
                const response = await fetch(API.CTF_STATS, {
                    method: 'GET',
                    credentials: 'include',
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`Stats endpoint returned ${response.status}`);
                }

                const data = await response.json();
                if (data?.status === 'error') {
                    throw new Error(data.message || 'Stats endpoint returned an error');
                }

                setStats({
                    globalPlace: normalize(data?.rating?.global_place),
                    countryPlace: normalize(data?.rating?.country_place),
                    totalPoints: normalize(data?.rating?.total_points),
                });
                setLoading(false);
            } catch (err) {
                if (err.name === 'AbortError') return;
                console.error('Error fetching CTF stats:', err);
                setError('Failed to load CTF statistics');
                setLoading(false);
            }
        };

        fetchStats();
        return () => controller.abort();
    }, []);

    if (loading) {
        return (
            <section className="landing-page-stats">
                <Container fluid>
                    <div className="landing-page-stats-loading">
                        <p>Loading CTF statistics...</p>
                        <div className="landing-page-stats-spinner"></div>
                    </div>
                </Container>
            </section>
        );
    }

    if (error) {
        return (
            <section className="landing-page-stats">
                <Container fluid>
                    <div className="landing-page-stats-error">
                        <p>{error}</p>
                        <button 
                            onClick={() => window.location.reload()} 
                            className="landing-page-stats-retry"
                        >
                            Retry
                        </button>
                    </div>
                </Container>
            </section>
        );
    }

    return (
        <section className="landing-page-stats">
            <Container fluid>
            <Row className="justify-content-center">
                    <Col md={10} lg={8} className="text-center mb-5">
                        <h2 className="landing-page-stats-title high-tech-font">Our CTF Journey</h2>
                        <div className="landing-page-stats-underline"></div>
                        <p className="landing-page-stats-subtitle">Track our progress on CTFtime</p>
                    </Col>
                </Row>

                <Row className="justify-content-center g-4">
                    <Col md={4}>
                        <div className="landing-page-stats-card">
                            <div className="landing-page-stats-icon-wrapper">
                                <FontAwesomeIcon icon={faRankingStar} className="landing-page-stats-icon" />
                            </div>
                            <h3 className="landing-page-stats-card-title">Global Ranking</h3>
                            <p className="landing-page-stats-value">
                                {stats?.globalPlace != null ? `#${stats.globalPlace}` : 'N/A'}
                            </p>
                        </div>
                    </Col>

                    <Col md={4}>
                        <div className="landing-page-stats-card">
                            <div className="landing-page-stats-icon-wrapper">
                                <FontAwesomeIcon icon={faTrophy} className="landing-page-stats-icon" />
                            </div>
                            <h3 className="landing-page-stats-card-title">Total Rating Points</h3>
                            <p className="landing-page-stats-value">
                                {stats?.totalPoints != null ? stats.totalPoints.toFixed(2) : 'N/A'}
                            </p>
                        </div>
                    </Col>

                    <Col md={4}>
                        <div className="landing-page-stats-card">
                            <div className="landing-page-stats-icon-wrapper">
                                <FontAwesomeIcon icon={faChartLine} className="landing-page-stats-icon" />
                            </div>
                            <h3 className="landing-page-stats-card-title">Country Rank</h3>
                            <p className="landing-page-stats-value">
                                {stats?.countryPlace != null ? `#${stats.countryPlace}` : 'N/A'}
                            </p>
                        </div>
                    </Col>
                </Row>

                <Row className="justify-content-center mt-4">
                    <Col md={6} className="text-center">
                        <a 
                            href="https://ctftime.org/team/358628/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="landing-page-stats-link"
                        >
                            View Full Profile on CTFtime
                        </a>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

export default CTFStatsSection;